import { useContext, useState, useRef, useEffect } from "react";
import AnimationContainer from "../../UI/AnimationContainer";
import styles from "./OrderForm.module.css";
import Input from "../../UI/Input";
import useSendForm from "../../Hooks/use-send-form";
import DataContext from "../../store/data-context";
import List from "./List";
import ReCAPTCHA from "react-google-recaptcha";
import useVerifyCaptcha from "../../Hooks/use-verify-captcha";
import ToggleButton from "../../UI/ToggleButton";
import logo from "../../../assets/logo.png";
import InSiteLogo from "../../UI/InSiteLogo";
import Success from "./Success";

export default function OrderForm() {
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState(false);

    const [surname, setSurname] = useState("");
    const [surnameError, setSurnameError] = useState(false);

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(false);

    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState(false);

    const [description, setDescription] = useState("");
    const [descriptionError, setDescriptionError] = useState(false);

    const [isCompany, setIsCompany] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [companyNameError, setCompanyNameError] = useState(false);

    const [companyID, setCompanyID] = useState("");
    const [companyIDError, setCompanyIDError] = useState(false);

    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [agreementAcceptedError, setAgreemenAcceptedError] = useState(false);

    const { isVerified: captchaVerified, verifyCaptcha } = useVerifyCaptcha();
    const [captchaEnabled, setCaptchaEnabled] = useState(false);

    const { sendForm } = useSendForm();

    const {
        selectedService,
        selectedTv,
        selectedPhone,
        location,
        formSubmitted,
        setFormSubmitted,
        setLoading,
        setError,
        selectedAttributes,
        additionalOptions,
        setInHelp,
        formSent,
    } = useContext(DataContext);

    useEffect(() => {
        setInHelp({
            title: "Pregled i ispunjavanje upita",
            description:
                "Molimo vas da ispunite upit s potrebnim podacima kako bismo obradili vaš zahtjev. Nakon što završite, kliknite na gumb 'Pošalji upit' za slanje vašeg upita.",
            active: false,
            quickTip: true,
            questions: [],
        });
    }, []);

    const reCaptchaRef = useRef();

    const submitForm = async () => {
        setLoading(true);
        let isAnyError = false;

        if (name.length < 1 || name.length > 50) {
            setNameError(true);
            isAnyError = true;
        }

        if (surname.length < 1 || surname.length > 50) {
            setSurnameError(true);
            isAnyError = true;
        }

        if (
            email.length < 1 ||
            email.length > 50 ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ) {
            setEmailError(true);
            isAnyError = true;
        }

        if (phone.length < 6 || phone.length > 14) {
            setPhoneError(true);
            isAnyError = true;
        }

        if (description.length > 1000) {
            setDescriptionError(true);
            isAnyError = true;
        }

        if (isCompany) {
            if (companyName.length < 1 || companyName.length > 50) {
                setCompanyNameError(true);
                isAnyError = true;
            }

            if (companyID.length < 10 || companyID.length >= 20) {
                setCompanyIDError(true);
                isAnyError = true;
            }
        }

        if (!agreementAccepted) {
            setAgreemenAcceptedError(true);
            isAnyError = true;
        }

        if (!isAnyError) {
            let user_data = {
                name,
                surname,
                email,
                phone,
                description: description.length > 0 ? description : "-",
                companyName: isCompany ? companyName : "-",
                companyID: isCompany ? companyID : "-",
            };

            const mojaTV = additionalOptions.find((el) => el.name === "MojaTV");
            const telefon = additionalOptions.find(
                (el) => el.name === "Telefon"
            );

            const data = {
                user: user_data,
                address: location.title,
                coordinates: location.position,
                internet: selectedService ? selectedService.name : "-",
                tv: mojaTV.value === 0 ? "-" : mojaTV.value,
                phone: telefon.value === 0 ? "-" : telefon.value,
                questions:
                    selectedAttributes.findIndex((el) => el.active === true) !==
                    -1
                        ? selectedAttributes
                              .filter((el) => el.active === true)
                              .map((el) => el.name)
                        : ["-"],
            };

            const token = await reCaptchaRef.current.executeAsync();

            const response = await verifyCaptcha(token);

            if (response) {
                reCaptchaRef.current.reset();
                sendForm(data);
                setFormSubmitted(false);
            } else {
                reCaptchaRef.current.reset();
                console.log("CAPTCHA ERROR");
                setLoading(false);
                setError({
                    message:
                        "Problem sa slanjem upita. Pokušajte ponovo kasnije.",
                    action: "zatvori",
                });
                setFormSubmitted(false);
            }
        } else {
            setFormSubmitted(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            setCaptchaEnabled(true);
        }, [1500]);
    });

    useEffect(() => {
        formSubmitted && submitForm();
    }, [formSubmitted]);

    return (
        <>
            {formSent && <Success />}
            <div className={styles.container}>
                <InSiteLogo />

                <List
                    selectedService={selectedService}
                    selectedPhone={selectedPhone}
                    selectedTv={selectedTv}
                    additionalOptions={additionalOptions}
                />

                <form className={styles.form} onSubmit={submitForm}>
                    <div className={styles[`form-inputs`]}>
                        <div className={styles[`main-info`]}>
                            <div className={styles.name}>
                                <Input
                                    value={name}
                                    setValue={setName}
                                    error={nameError}
                                    setError={setNameError}
                                    label="Ime"
                                />
                                <Input
                                    value={surname}
                                    setValue={setSurname}
                                    error={surnameError}
                                    setError={setSurnameError}
                                    label="Prezime"
                                />
                            </div>
                            <div className={styles.contacts}>
                                <Input
                                    value={email}
                                    setValue={setEmail}
                                    error={emailError}
                                    setError={setEmailError}
                                    label="Email"
                                />
                                <Input
                                    value={phone}
                                    setValue={setPhone}
                                    error={phoneError}
                                    setError={setPhoneError}
                                    label="Broj telefona"
                                />
                            </div>
                        </div>
                        <div className={styles.company}>
                            <div
                                className={`${styles[`is-company`]} ${
                                    styles[isCompany && `is-company-active`]
                                }`}
                            >
                                <div className={styles[`company-info`]}>
                                    <Input
                                        value={companyName}
                                        setValue={setCompanyName}
                                        error={companyNameError}
                                        setError={setCompanyNameError}
                                        label="Naziv tvrtke"
                                        disabled={!isCompany}
                                    />
                                    <Input
                                        value={companyID}
                                        setValue={setCompanyID}
                                        error={companyIDError}
                                        setError={setCompanyIDError}
                                        label="OIB tvrtke"
                                        disabled={!isCompany}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.description}>
                        <Input
                            value={description}
                            setValue={setDescription}
                            label="Napomena"
                            textArea={true}
                            error={descriptionError}
                            setError={setDescriptionError}
                        ></Input>
                    </div>

                    <div className={styles[`checkbox-area`]}>
                        <label className={styles[`checkbox`]}>
                            <input
                                type="checkbox"
                                checked={isCompany}
                                onChange={(e) => setIsCompany(e.target.checked)}
                                className={styles.chx}
                            />
                            Trebam R1
                        </label>

                        <label
                            className={`${styles.checkbox} ${
                                styles[agreementAcceptedError && `chx-error`]
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={agreementAccepted}
                                onChange={(e) => {
                                    setAgreemenAcceptedError(false);
                                    setAgreementAccepted(e.target.checked);
                                }}
                                className={`${styles.chx}`}
                            />
                            Prihvaćam da ova web stranica pohranjuje moje
                            poslane podatke kako bi mogli odgovoriti na moj
                            upit.
                        </label>
                    </div>
                </form>

                {captchaEnabled && (
                    <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        size="invisible"
                        onError={() => console.log("CAPTCHA ERROR")}
                        ref={reCaptchaRef}
                    />
                )}
            </div>
        </>
    );
}
